import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Paper,
  Chip,
  Tooltip,
  Badge,
  Stack,
  alpha,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AddIcon from '@mui/icons-material/Add';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { groupsApi } from '../services/api';

const GroupsManagement = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openNotifyDialog, setOpenNotifyDialog] = useState(false);
  const [openUsersDialog, setOpenUsersDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupUsers, setGroupUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [notification, setNotification] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [viewMode, setViewMode] = useState('list');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [formData, setFormData] = useState({
    groupName: '',
    groupTag: '',
    groupSerialKey: '',
    groupFirebaseTopicName: '',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await groupsApi.getGroups();
      setGroups(response.data.groups || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      groupName: '',
      groupTag: '',
      groupSerialKey: '',
      groupFirebaseTopicName: '',
    });
  };

  const handleOpenNotifyDialog = (group) => {
    setSelectedGroup(group);
    setOpenNotifyDialog(true);
  };

  const handleCloseNotifyDialog = () => {
    setOpenNotifyDialog(false);
    setSelectedGroup(null);
    setNotification('');
  };

  const handleOpenUsersDialog = async (group) => {
    setSelectedGroup(group);
    setOpenUsersDialog(true);
    setLoadingUsers(true);
    try {
      const response = await groupsApi.getGroupUsers(group.groupTag);
      const sortedUsers = [...(response.data.users || [])]
        .filter(user => user && user.serial_android_id)
        .sort((a, b) => (a.serial_android_id || '').localeCompare(b.serial_android_id || ''));
      setGroupUsers(sortedUsers);
      showSnackbar(`Found ${response.data.count} users in this group`);
    } catch (error) {
      console.error('Error fetching group users:', error);
      showSnackbar('Failed to fetch group users', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCloseUsersDialog = () => {
    setOpenUsersDialog(false);
    setSelectedGroup(null);
    setGroupUsers([]);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await groupsApi.createGroup(formData);
      fetchGroups();
      handleCloseDialog();
      showSnackbar('Group created successfully');
    } catch (error) {
      console.error('Error creating group:', error);
      showSnackbar('Failed to create group', 'error');
    }
  };

  const handleDeleteClick = (group) => {
    setSelectedGroup(group);
    setDeleteDialogOpen(true);
    setDeleteConfirmText('');
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmText !== 'DELETE') {
      showSnackbar('Please type DELETE to confirm', 'error');
      return;
    }

    try {
      await groupsApi.deleteGroup(selectedGroup.id);
      setDeleteDialogOpen(false);
      setSelectedGroup(null);
      setDeleteConfirmText('');
      fetchGroups();
      showSnackbar('Group deleted successfully');
    } catch (error) {
      console.error('Error deleting group:', error);
      showSnackbar('Failed to delete group', 'error');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedGroup(null);
    setDeleteConfirmText('');
  };

  const handleSendNotification = async () => {
    try {
      await groupsApi.sendNotification(selectedGroup.id, notification);
      handleCloseNotifyDialog();
      showSnackbar('Notification sent successfully');
    } catch (error) {
      console.error('Error sending notification:', error);
      showSnackbar('Failed to send notification', 'error');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3, 
          background: theme => `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
          borderRadius: 3,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <GroupsRoundedIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Groups Management
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage your groups, send notifications, and track users
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newValue) => newValue && setViewMode(newValue)}
              size="small"
              sx={{
                backgroundColor: theme => theme.palette.background.paper,
                '& .MuiToggleButton-root.Mui-selected': {
                  backgroundColor: theme => alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                },
              }}
            >
              <ToggleButton value="grid">
                <GridViewRoundedIcon />
              </ToggleButton>
              <ToggleButton value="list">
                <ViewListRoundedIcon />
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
              }}
            >
              New Group
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Groups Grid/List */}
      {viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {groups.map((group) => (
            <Grid item xs={12} sm={6} md={4} key={group.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.15)}`,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Avatar 
                      sx={{ 
                        bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                        color: 'primary.main',
                      }}
                    >
                      {group.groupName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="h6" component="div">
                      {group.groupName}
                    </Typography>
                  </Box>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Group Tag
                      </Typography>
                      <Chip 
                        label={group.groupTag}
                        size="small"
                        sx={{ 
                          bgcolor: theme => alpha(theme.palette.info.main, 0.1),
                          color: 'info.main',
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Serial Key
                      </Typography>
                      <Chip 
                        icon={<KeyRoundedIcon />}
                        label={group.groupSerialKey}
                        size="small"
                        sx={{ 
                          bgcolor: theme => alpha(theme.palette.warning.main, 0.1),
                          color: 'warning.main',
                        }}
                      />
                    </Box>
                  </Stack>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', gap: 1, p: 2 }}>
                  <Tooltip title="View Users">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenUsersDialog(group)}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main', bgcolor: theme => alpha(theme.palette.primary.main, 0.1) },
                      }}
                    >
                      <PersonRoundedIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Send Notification">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenNotifyDialog(group)}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': { color: 'info.main', bgcolor: theme => alpha(theme.palette.info.main, 0.1) },
                      }}
                    >
                      <NotificationsIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Group">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(group)}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': { color: 'error.main', bgcolor: theme => alpha(theme.palette.error.main, 0.1) },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <List>
            {groups.map((group, index) => (
              <React.Fragment key={group.id}>
                <ListItem
                  sx={{
                    py: 2,
                    '&:hover': {
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar 
                      sx={{ 
                        bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                        color: 'primary.main',
                      }}
                    >
                      {group.groupName.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="subtitle1">
                          {group.groupName}
                        </Typography>
                        <Chip 
                          label={group.groupTag}
                          size="small"
                          sx={{ 
                            bgcolor: theme => alpha(theme.palette.info.main, 0.1),
                            color: 'info.main',
                          }}
                        />
                        <Chip 
                          icon={<KeyRoundedIcon />}
                          label={group.groupSerialKey}
                          size="small"
                          sx={{ 
                            bgcolor: theme => alpha(theme.palette.warning.main, 0.1),
                            color: 'warning.main',
                          }}
                        />
                      </Box>
                    }
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Users">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenUsersDialog(group)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main', bgcolor: theme => alpha(theme.palette.primary.main, 0.1) },
                        }}
                      >
                        <PersonRoundedIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Send Notification">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenNotifyDialog(group)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'info.main', bgcolor: theme => alpha(theme.palette.info.main, 0.1) },
                        }}
                      >
                        <NotificationsIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Group">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(group)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'error.main', bgcolor: theme => alpha(theme.palette.error.main, 0.1) },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
                {index < groups.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Create Group Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <AddIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6">Create New Group</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Group Name"
              fullWidth
              value={formData.groupName}
              onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
            />
            <TextField
              label="Group Tag"
              fullWidth
              value={formData.groupTag}
              onChange={(e) => setFormData({ ...formData, groupTag: e.target.value })}
            />
            <TextField
              label="Serial Key"
              fullWidth
              value={formData.groupSerialKey}
              onChange={(e) => setFormData({ ...formData, groupSerialKey: e.target.value })}
            />
            <TextField
              label="Firebase Topic Name"
              fullWidth
              value={formData.groupFirebaseTopicName}
              onChange={(e) => setFormData({ ...formData, groupFirebaseTopicName: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={<AddIcon />}
            sx={{ textTransform: 'none' }}
          >
            Create Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <DeleteIcon sx={{ color: 'error.main' }} />
            <Typography variant="h6">Delete Group</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              Are you sure you want to delete the group "{selectedGroup?.groupName}"? This action cannot be undone.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Type DELETE to confirm:
            </Typography>
            <TextField
              fullWidth
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              error={deleteConfirmText !== '' && deleteConfirmText !== 'DELETE'}
              helperText={deleteConfirmText !== '' && deleteConfirmText !== 'DELETE' ? 'Please type DELETE to confirm' : ''}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleDeleteCancel} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteConfirmText !== 'DELETE'}
            startIcon={<DeleteIcon />}
            sx={{ textTransform: 'none' }}
          >
            Delete Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Notification Dialog */}
      <Dialog open={openNotifyDialog} onClose={handleCloseNotifyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <NotificationsIcon sx={{ color: 'info.main' }} />
            <Typography variant="h6">Send Notification</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Send a notification to all users in the group "{selectedGroup?.groupName}"
            </Typography>
            <TextField
              label="Message"
              multiline
              rows={4}
              fullWidth
              value={notification}
              onChange={(e) => setNotification(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseNotifyDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendNotification}
            disabled={!notification.trim()}
            startIcon={<SendRoundedIcon />}
            sx={{ textTransform: 'none' }}
          >
            Send Notification
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Users Dialog */}
      <Dialog open={openUsersDialog} onClose={handleCloseUsersDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <PersonRoundedIcon sx={{ color: 'primary.main' }} />
            <Box>
              <Typography variant="h6">Group Users</Typography>
              <Typography variant="body2" color="text.secondary">
                Users in group "{selectedGroup?.groupName}"
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingUsers ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : groupUsers.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
              <PersonRoundedIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
              <Typography color="text.secondary">No users found in this group</Typography>
            </Box>
          ) : (
            <List>
              {(groupUsers || []).map((user, index) => (
                <React.Fragment key={user?.id || index}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar 
                        sx={{
                          bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                        }}
                      >
                        {user?.serial_android_id ? user.serial_android_id.charAt(0).toUpperCase() : '?'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={user?.serial_android_id || 'Unknown Device'}
                      secondary={user?.groupTag || 'No group tag'}
                    />
                  </ListItem>
                  {index < groupUsers.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseUsersDialog} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GroupsManagement;
